import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { trpc } from './trpc'

/**
 * Abfragen als wiederverwendbare Optionen.
 *
 * Bewusst der nackte tRPC-Client in `queryFn` statt einer weiteren
 * Integrationsschicht: die Typen kommen ohnehin vollständig aus dem Backend,
 * und es bleibt ein bewegliches Teil weniger (Konzept Kap. 8.1).
 *
 * Leitplanke 2: `tasks.all` lädt den Gesamtbestand einmal. Filtern und Suchen
 * passiert danach lokal, ohne Serverabfrage. `staleTime: Infinity` – aktuell
 * gehalten wird über WebSocket, nicht durch erneutes Abfragen.
 */

/**
 * Wer ist angemeldet. Entscheidet, ob die Shell oder die Anmeldemaske erscheint.
 * `retry: false`, weil „nicht angemeldet" eine gültige Antwort ist und kein
 * Fehler, den man wiederholen müsste.
 */
/**
 * **Wie man hereinkommt** (05.11.2026, [E-461](../../../docs/ENTSCHEIDUNGEN.md#e-461)): nur über
 * NetCon.Cloud oder mit Maske. Ändert sich nur mit einem Neustart des Servers – einmal je Laden.
 */
export const anmeldungQuery = queryOptions({
  queryKey: ['auth', 'anmeldung'],
  queryFn: () => trpc.auth.anmeldung.query(),
  staleTime: Infinity,
  /** Scheitert die Frage, erscheint die Maske – ein Passwort weist der Server dann mit Grund ab. */
  retry: false,
})

export const sitzungQuery = queryOptions({
  queryKey: ['auth', 'me'],
  queryFn: () => trpc.auth.me.query(),
  staleTime: 60_000,
  retry: false,
  /**
   * **Wer zurück an den Reiter kommt, erfährt, ob er noch angemeldet ist** (17.09.2026).
   *
   * Global ist `refetchOnWindowFocus` aus (der Bestand kommt über den WebSocket). Für die
   * Sitzung hieß das: über Nacht abgelaufen, am Morgen stand die Anwendung da wie gestern – und
   * wer nur las, merkte nichts, bis ein Klick still zurückgerollt wurde. Eine kleine Abfrage,
   * höchstens einmal je `staleTime`.
   */
  refetchOnWindowFocus: true,
})

/** Gesamtbestand der Aufgaben – siehe Leitplanke 2 oben. */
export const aufgabenQuery = queryOptions({
  queryKey: ['tasks', 'all'],
  queryFn: () => trpc.tasks.all.query(),
  staleTime: Infinity,
})

export const personenQuery = queryOptions({
  queryKey: ['people', 'all'],
  queryFn: () => trpc.people.all.query(),
  staleTime: Infinity,
})

/** Eine Person, wie die Auswahllisten sie sehen – mit ihren Arbeitswelten (Z-07). */
export type Person = Awaited<ReturnType<typeof trpc.people.all.query>>[number]

/**
 * **Meine Workspaces** ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md)).
 *
 * `staleTime: Infinity`: die Liste ändert sich nur, wenn der Admin etwas zuteilt oder jemand
 * sich einen persönlichen Workspace anlegt – beides sind ausdrückliche Handlungen, nach denen
 * die Abfrage verworfen wird. Regelmäßig nachzufragen hieße, bei jedem Seitenwechsel eine
 * Antwort zu holen, die seit Wochen dieselbe ist.
 *
 * **Nicht workspace-abhängig geschlüsselt.** Der Schlüssel enthält bewusst *nicht* den aktiven
 * Workspace: die Liste ist für alle Workspaces dieselbe, und ein Schlüssel, der beim
 * Umschalten wechselt, ließe den Umschalter beim Umschalten leer laufen – genau in dem
 * Augenblick, in dem er seine Einträge braucht.
 */
export const workspacesQuery = queryOptions({
  queryKey: ['workspaces', 'meine'],
  queryFn: () => trpc.workspaces.meine.query(),
  staleTime: Infinity,
})

/** Ein Workspace, wie die Oberfläche ihn sieht – mit Art und eigenem WIP-Limit. */
export type WorkspaceEintrag = Awaited<ReturnType<typeof trpc.workspaces.meine.query>>[number]

/**
 * **Die eigene Abnahme-Erinnerung** (Z-09). Eine Zeile je Person, zweimal im Jahr angefasst –
 * deshalb `staleTime: Infinity`: neu gelesen wird sie nur, wenn sie jemand ändert.
 */
/**
 * **Die Microsoft-Verbindung der angemeldeten Person** (Z-17). `staleTime: Infinity`, weil sie
 * sich nur ändert, wenn jemand verbindet, trennt oder ein Abgleich läuft – und das wird an
 * diesen Stellen ausdrücklich nachgeladen.
 */
export const microsoftStatusQuery = queryOptions({
  queryKey: ['microsoft', 'status'],
  queryFn: () => trpc.microsoft.status.query(),
  staleTime: Infinity,
})

/** Wer verbunden ist – nur für die Administration (Einrichtungskarte). */
export const microsoftVerbundeneQuery = queryOptions({
  queryKey: ['microsoft', 'verbundene'],
  queryFn: () => trpc.microsoft.verbundene.query(),
  staleTime: 60_000,
})

/** Die heutigen Termine – live aus dem Kalender; fünf Minuten sind für einen Kalender aktuell genug. */
export const termineHeuteQuery = queryOptions({
  queryKey: ['microsoft', 'termineHeute'],
  queryFn: () => trpc.microsoft.termineHeute.query(),
  staleTime: 5 * 60_000,
})

/**
 * **Die Termine eines bestimmten Tages** – für den Tagesplan, durch den man blättern kann
 * (Z-19, 30.09.2026).
 *
 * Eigener Schlüssel je Tag, damit das Zurückblättern aus dem Zwischenspeicher kommt statt aus
 * dem Netz. `staleTime` wie bei {@link termineHeuteQuery}: Ein Kalender ändert sich, aber nicht
 * im Sekundentakt.
 */
export const termineAmTagQuery = (tag: string) =>
  queryOptions({
    queryKey: ['microsoft', 'termineAmTag', tag],
    queryFn: () => trpc.microsoft.termineAmTag.query({ tag }),
    staleTime: 5 * 60_000,
  })

/**
 * **Die Termine der nächsten Wochen** – für die Vorschau „schaffe ich das noch" (Z-19 AP 19.5).
 *
 * **Ein** Aufruf für die ganze Spanne, nach Tagen gebündelt. Vierzehn Einzelabfragen für
 * vierzehn Tage wären gegen Leitplanke 2; dass die Vorschau deshalb bisher mit einem
 * durchschnittlichen Tag rechnete, war die ehrliche, aber zu optimistische Notlösung.
 *
 * `staleTime` länger als beim einzelnen Tag: Diese Zahlen tragen eine Warnung über Wochen, und
 * eine Besprechung, die vor zehn Minuten eingetragen wurde, ändert daran nichts.
 */
export const termineVorschauQuery = (von: string, bis: string) =>
  queryOptions({
    queryKey: ['microsoft', 'termineImZeitraum', von, bis],
    queryFn: () => trpc.microsoft.termineImZeitraum.query({ von, bis }),
    staleTime: 15 * 60_000,
  })

/**
 * **Die eigene Arbeitszeit** (Z-19 AP 19.4). Eigene Abfrage statt eines Feldes an der Sitzung:
 * Vier Spalten, die nur der Tagesplan braucht, gehören nicht in den Pfad, der bei jedem Aufruf
 * läuft. `staleTime` großzügig – wer seine Arbeitszeit ändert, tut das nicht stündlich.
 */
export const meineArbeitszeitQuery = queryOptions({
  queryKey: ['people', 'meineArbeitszeit'],
  queryFn: () => trpc.people.meineArbeitszeit.query(),
  staleTime: 10 * 60_000,
})

export const meineErinnerungQuery = queryOptions({
  queryKey: ['people', 'meineErinnerung'],
  queryFn: () => trpc.people.meineErinnerung.query(),
  staleTime: Infinity,
})

/**
 * **Meine Vorwarnungen** (Z-13) – die eigene Einstellung und die abonnierten Aufgaben.
 *
 * Kurze Frist statt `Infinity`, anders als bei der Abnahme-Erinnerung: hier steckt neben der
 * Einstellung eine **Liste** darin, und die ändert sich, sobald jemand irgendwo ein Häkchen
 * setzt – auch ein anderer, wenn er „alle Bearbeiter" wählt. Eine Liste, die bis zum Neuladen
 * der Seite steht, wäre in „Klopft an" eine Zeile, die es nicht mehr gibt.
 *
 * **Ohne Termine.** Die rechnet der Client aus dem Bestand, den er ohnehin hält (Leitplanke 2),
 * mit derselben Funktion wie der Server (`wirksamerTermin`).
 */
export const meineVorwarnungQuery = queryOptions({
  queryKey: ['vorwarnung', 'meins'],
  queryFn: () => trpc.vorwarnung.meins.query(),
  staleTime: 30_000,
})

export type MeineVorwarnung = Awaited<ReturnType<typeof trpc.vorwarnung.meins.query>>

/* ── Pools (Phase 4) ────────────────────────────────────────────────── */

/**
 * **Die Pools einer bestimmten Arbeitswelt** (28.08.2026).
 *
 * Nils, mit einem Bild des Aufgabendetails: *„da wechseln sich die Details nicht, wenn man den
 * Workspace ändert. Bzw. generell in den Aufgabendetails."* Die Auswahllisten kamen aus der
 * **aktiven** Welt – für eine Ansicht richtig, für ein Detail falsch: eine Aufgabe liegt, wo sie
 * liegt, und aus „Mein Tag" heraus ist das selten die Welt des Umschalters.
 *
 * Der Schlüssel trägt die Welt mit, sonst zeigte der Zwischenspeicher die Liste der zuletzt
 * geöffneten Aufgabe weiter.
 */
export const poolsDerWeltQuery = (workspaceId: string | null | undefined) =>
  queryOptions({
    queryKey: ['pools', 'all', workspaceId ?? 'aktiv'],
    queryFn: () => trpc.pools.all.query(workspaceId ? { workspaceId } : undefined),
    staleTime: 15_000,
  })

/** Dasselbe für Vorhaben – siehe {@link poolsDerWeltQuery}. */
export const vorhabenDerWeltQuery = (workspaceId: string | null | undefined) =>
  queryOptions({
    queryKey: ['vorhaben', 'alle', workspaceId ?? 'aktiv'],
    queryFn: () => trpc.vorhaben.alle.query(workspaceId ? { workspaceId } : undefined),
    staleTime: 15_000,
  })

export const poolsQuery = queryOptions({
  queryKey: ['pools', 'all'],
  queryFn: () => trpc.pools.all.query(),
  staleTime: 30_000,
})

/**
 * **Der Vorrat über alle meine Arbeitswelten** – für „Mein Tag"
 * ([E-274](../../../../docs/ENTSCHEIDUNGEN.md#e-274)) und für den **Eingang**
 * ([E-296](../../../../docs/ENTSCHEIDUNGEN.md#e-296), 01.09.2026: auch er geht über alle Welten,
 * und die Pool-Auswahl darin muss dieselbe Reichweite haben wie die Liste).
 *
 * Nils, 28.08.2026: *„Mein Tag ist doch eigentlich workspace losgelöst. bei den vorschlägen usw.
 * da stehen aber nur die aus dem gewählten workspace?"*
 *
 * Eine **eigene** Abfrage und nicht ein Zusatz an `poolsQuery`: die Pools-Ansicht bleibt an die
 * aktive Welt gebunden, und beide dieselbe Antwort im Zwischenspeicher teilen zu lassen hieße,
 * dass die zuletzt geladene gewinnt. Der Schlüssel beginnt trotzdem mit `['pools']`, damit jede
 * bestehende Entwertung (`invalidateQueries({ queryKey: ['pools'] })`) ihn mitnimmt – sonst
 * bliebe „Mein Tag" nach dem Ziehen einer Aufgabe auf einem alten Vorrat stehen.
 */
export const poolsAllerWeltenQuery = queryOptions({
  queryKey: ['pools', 'alle-welten'],
  queryFn: () => trpc.pools.all.query({ alleWelten: true }),
  staleTime: 30_000,
})

export type Pool = Awaited<ReturnType<typeof trpc.pools.all.query>>[number]

/** Wen für eine Aufgabe vorschlagen (AP 4.6). */
export const vorschlagQuery = (taskId: string) =>
  queryOptions({
    queryKey: ['people', 'vorschlag', taskId],
    queryFn: () => trpc.people.vorschlagFuer.query({ taskId }),
    staleTime: 15_000,
  })

export const quellenQuery = queryOptions({
  queryKey: ['system', 'sources'],
  queryFn: () => trpc.system.sources.query(),
  staleTime: 60_000,
})

/**
 * **Wie frisch der Bestand ist** ([E-312](../../../../docs/ENTSCHEIDUNGEN.md#e-312)).
 *
 * Eine Minute Frist: die Zahl unten links zählt ohnehin im Halbminutentakt weiter, und ein
 * Rundlauf je Sekunde für eine Angabe, die sich alle fünf Minuten ändert, wäre Verschwendung.
 */
export const abgleichQuery = queryOptions({
  queryKey: ['system', 'abgleich'],
  queryFn: () => trpc.system.abgleich.query(),
  staleTime: 60_000,
  /**
   * **Fünf Minuten, nicht eine** (15.10.2026, E-410). Gemessen war das die häufigste Abfrage der
   * ganzen Anwendung: 730 am Tag. Jeder Abgleich meldet sich ohnehin über den Live-Kanal
   * (`sync.geaendert` verwirft `['system']`), der Takt ist nur das Netz darunter – und „vor
   * 3 Min." rechnet die Seitenleiste selbst weiter.
   */
  refetchInterval: 5 * 60_000,
})

export const syncQuery = queryOptions({
  queryKey: ['system', 'sources'],
  queryFn: () => trpc.system.sources.query(),
  staleTime: 30_000,
})

export const bestandQuery = queryOptions({
  queryKey: ['system', 'bestand'],
  queryFn: () => trpc.system.bestand.query(),
  staleTime: 30_000,
})

export const jobFehlerQuery = queryOptions({
  queryKey: ['system', 'jobFehler'],
  queryFn: () => trpc.system.jobFehler.query(),
  staleTime: 30_000,
})

export const queueQuery = queryOptions({
  queryKey: ['system', 'queue'],
  queryFn: () => trpc.system.queue.query(),
  staleTime: 10_000,
})

/** Angemeldete Person, wie das Backend sie liefert. */
export type Sitzung = NonNullable<Awaited<ReturnType<typeof trpc.auth.me.query>>>

export type Aufgabe = Awaited<ReturnType<typeof trpc.tasks.all.query>>[number]

/* ── Auswertungen (Phase 2) ─────────────────────────────────────────────
 *
 * Nur für die Leitung – serverseitig durchgesetzt (E-19). Der Client blendet
 * zusätzlich aus; wer die Route direkt aufruft, bekommt FORBIDDEN.
 *
 * `staleTime` bewusst großzügig und `placeholderData: keepPreviousData` bei den
 * zeitraumabhängigen Abfragen: beim Umschalten von 7 auf 14 Tage soll die alte
 * Auswertung stehen bleiben statt gegen einen Spinner getauscht zu werden
 * (Leitplanke 1). Aktuell gehalten wird über WebSocket.
 */

export const erledigtProTagQuery = (tage: number) =>
  queryOptions({
    queryKey: ['analytics', 'erledigtProTag', tage],
    queryFn: () => trpc.analytics.erledigtProTag.query({ tage }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })

export const zeitleisteQuery = (vonTag: string, bisTag: string) =>
  queryOptions({
    queryKey: ['analytics', 'zeitleiste', vonTag, bisTag],
    queryFn: () => trpc.analytics.zeitleiste.query({ vonTag, bisTag }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })

export const liegezeitenQuery = queryOptions({
  queryKey: ['analytics', 'liegezeiten'],
  queryFn: () => trpc.analytics.liegezeiten.query(),
  staleTime: 60_000,
})

/** Offene Aufgaben ausgeschiedener Personen – siehe Leitstand. */
export const verwaisteQuery = queryOptions({
  queryKey: ['analytics', 'verwaist'],
  queryFn: () => trpc.analytics.verwaisteAufgaben.query(),
  staleTime: 60_000,
})

/** Ereignisse einer Person an einem Tag – Datengrundlage des Tages-Popups. */
export const tagesEreignisseQuery = (personId: string | null, tag: string) =>
  queryOptions({
    queryKey: ['analytics', 'tagesEreignisse', personId, tag],
    queryFn: () => trpc.analytics.tagesEreignisse.query({ personId, tag }),
    staleTime: 60_000,
  })

/* ── Einzelne Aufgabe (Lesefassung im Tages-Popup) ──────────────────── */

export const aufgabeQuery = (id: string) =>
  queryOptions({
    queryKey: ['tasks', 'byId', id],
    queryFn: () => trpc.tasks.byId.query({ id }),
    staleTime: 60_000,
  })

/** Alles zu einer Aufgabe in einem Zug – Grundlage des Aufgabendetails (AP 4.5). */
/** tRPC puts the server's error code on `data.code`; anything without one never reached the API. */
export const trpcFehlerCode = (fehler: unknown): string | undefined =>
  (fehler as { data?: { code?: string } } | null)?.data?.code

export const aufgabeDetailQuery = (id: string) =>
  queryOptions({
    queryKey: ['tasks', 'detail', id],
    queryFn: () => trpc.tasks.detail.query({ id }),
    staleTime: 15_000,
    /**
     * **A restart is not a missing task** (16.09.2026). While the API restarts (~20 s), nginx
     * answers 502 – with the global `retry: 1` the dialog gave up after a second and said
     * "Aufgabe nicht gefunden". Answers from the API itself stay final; everything else is
     * retried with backoff (1+2+4+8+16 s).
     */
    retry: (versuche, fehler) => trpcFehlerCode(fehler) === undefined && versuche < 5,
  })

export type AufgabeDetailDaten = Awaited<ReturnType<typeof trpc.tasks.detail.query>>

export const aufgabeRefsQuery = (id: string) =>
  queryOptions({
    queryKey: ['tasks', 'refs', id],
    queryFn: () => trpc.tasks.refs.query({ id }),
    staleTime: Infinity,
  })

export const aufgabeVerlaufQuery = (id: string) =>
  queryOptions({
    queryKey: ['tasks', 'history', id],
    queryFn: () => trpc.tasks.history.query({ id }),
    staleTime: 60_000,
  })

/* ── Benutzerverwaltung (nur admin) ─────────────────────────────────── */

/**
 * `retry: false`: „darf nicht" ist eine gültige Antwort und kein Fehler, den man
 * wiederholen müsste – sonst klopft ein Steuerer dreimal an eine Tür, die zu
 * bleibt.
 */
export const benutzerQuery = queryOptions({
  queryKey: ['people', 'verwaltung'],
  queryFn: () => trpc.people.verwaltung.query(),
  staleTime: 30_000,
  retry: false,
})

export type Benutzer = Awaited<ReturnType<typeof trpc.people.verwaltung.query>>[number]

/** Was eine Zusammenführung bewegen würde – vor dem Klick (F-14). */
export const zusammenfuehrenVorschauQuery = (zielId: string, quellenIds: string[]) =>
  queryOptions({
    queryKey: ['people', 'mergeVorschau', zielId, ...quellenIds],
    queryFn: () => trpc.people.zusammenfuehrenVorschau.query({ zielId, quellenIds }),
    retry: false,
  })

export type MergeVorschau = Awaited<ReturnType<typeof trpc.people.zusammenfuehrenVorschau.query>>

/* ── Abwesenheiten (Zusatzpaket Z-01) ───────────────────────────────────
 *
 * **Eine Abfrage für alle Ansichten**, wie bei `tasks.all` (Leitplanke 2). Der
 * Leitstand markiert abwesende Tage in der Zeitleiste, „Mein Tag" zeigt, wer
 * heute fehlt, der Zuweisen-Dialog warnt – alles aus **dieser** Liste, lokal
 * gerechnet. Eine Abfrage je Frage wäre bei zwölf Personen ein Dutzend Aufrufe
 * für ein paar hundert Zeilen.
 *
 * `staleTime: Infinity` wie beim Aufgabenbestand: aktuell hält es der WebSocket
 * über `sync.geaendert` ([E-26](../../../../docs/ENTSCHEIDUNGEN.md)) – nicht ein
 * Zeitgeber, der im Hintergrund nachfragt.
 */

export const abwesenheitenQuery = queryOptions({
  queryKey: ['absences', 'uebersicht'],
  queryFn: () => trpc.absences.uebersicht.query(),
  staleTime: Infinity,
})

export type AbwesenheitsDaten = Awaited<ReturnType<typeof trpc.absences.uebersicht.query>>
export type AbwesenheitsZeile = AbwesenheitsDaten['abwesenheiten'][number]

/** Zuordnung Intranet ↔ Work, nur `admin`. `retry: false` wie bei `benutzerQuery`. */
export const abwesenheitsZuordnungQuery = queryOptions({
  queryKey: ['absences', 'zuordnung'],
  queryFn: () => trpc.absences.zuordnung.query(),
  staleTime: 30_000,
  retry: false,
})

export type AbwesenheitsZuordnung = Awaited<ReturnType<typeof trpc.absences.zuordnung.query>>

/* ── NetCon-Zugang (Phase 3, AP 3.1) ────────────────────────────────────
 *
 * Beide nur `admin`, deshalb `retry: false` wie bei `benutzerQuery`: „darf nicht"
 * ist eine gültige Antwort und kein Fehler, den man wiederholen müsste.
 *
 * **Kein `staleTime: Infinity`** wie beim Aufgabenbestand: hier hängt nichts am
 * WebSocket, und der Stand kann sich durch einen Verbindungstest in einem anderen
 * Browser ändern. Dafür ist es eine Ansicht, die selten offen ist.
 */

export const netconZugangQuery = queryOptions({
  queryKey: ['netcon', 'zugang'],
  queryFn: () => trpc.netcon.zugang.query(),
  staleTime: 30_000,
  retry: false,
})

/**
 * Die Warteschlangen der API. Holt bei jedem Öffnen einen fremden Endpunkt – nicht
 * öfter: `staleTime` großzügig, damit ein Wechsel zwischen Ansichten nicht jedes
 * Mal eine Anmeldung an NetCon auslöst.
 */
export const netconWarteschlangenQuery = queryOptions({
  queryKey: ['netcon', 'warteschlangen'],
  queryFn: () => trpc.netcon.warteschlangen.query(),
  staleTime: 5 * 60_000,
  retry: false,
})

export type NetConZugang = Awaited<ReturnType<typeof trpc.netcon.zugang.query>>

/**
 * Der Postfach-Zugang für Benachrichtigungen (18.08.2026). Wie die NetCon-Karte nur
 * `admin`, deshalb `retry: false` – „darf nicht" ist eine gültige Antwort.
 */
export const mailZugangQuery = queryOptions({
  queryKey: ['mail', 'zugang'],
  queryFn: () => trpc.mail.zugang.query(),
  staleTime: 30_000,
  retry: false,
})

/**
 * Der Rückstand: was Work führt und das Quellsystem noch nicht weiß (Phase 5, AP 5.5).
 *
 * Kürzere `staleTime` als die übrigen Systemabfragen: die Liste ist die Vorschau vor
 * einem Schreibvorgang in ein fremdes System. Wer sie ansieht, will wissen, was **jetzt**
 * ansteht – eine Minute alte Auskunft wäre hier die falsche Art von Sparsamkeit.
 */
export const rueckstandQuery = queryOptions({
  queryKey: ['system', 'rueckstand'],
  queryFn: () => trpc.system.rueckstand.query(),
  staleTime: 10_000,
})

/**
 * Sync-Zustand einer einzelnen Aufgabe – für die Meta-Spalte im Detail (AP 5.5).
 *
 * Als Funktion, nicht als Konstante: die Abfrage hängt an einer Aufgabe, und der
 * Query-Key muss sie tragen, sonst zeigt die zweite geöffnete Aufgabe den Zustand der
 * ersten.
 */
export const syncZustandQuery = (taskId: string) =>
  queryOptions({
    queryKey: ['system', 'syncZustand', taskId],
    queryFn: () => trpc.system.syncZustand.query({ taskId }),
    staleTime: 10_000,
  })

/**
 * Der Eingang (Phase 6). Kürzere `staleTime` als der Bestand: hier arbeitet jemand
 * gerade, und eine Liste, die nach dem Einordnen noch die alten Zeilen zeigt, sieht aus,
 * als hätte der Klick nicht gewirkt.
 */
/**
 * **Immer über alle Arbeitswelten, in denen ich Mitglied bin** (01.09.2026,
 * [E-296](../../../docs/ENTSCHEIDUNGEN.md#e-296)).
 *
 * Hier stand bis dahin ein Schalter (`alleWelten`) und im Schlüssel sein Stand. Beides ist weg:
 * der Eingang ist nicht mehr an den Umschalter gebunden, also gibt es auch keine zwei Listen
 * mehr, die sich im Zwischenspeicher auseinanderhalten müssten.
 */
export const eingangListeQuery = (
  sortierung: 'alter' | 'neueste' | 'prioritaet' | 'vorschlaege' = 'alter',
) =>
  queryOptions({
    queryKey: ['eingang', 'liste', sortierung],
    queryFn: () => trpc.eingang.liste.query({ sortierung }),
    staleTime: 5_000,
  })

export type EingangZeile = Awaited<ReturnType<typeof trpc.eingang.liste.query>>[number]

export const eingangKanaeleQuery = queryOptions({
  queryKey: ['eingang', 'kanaele'],
  queryFn: () => trpc.eingang.kanaele.query(),
  staleTime: 30_000,
})

/**
 * KI-Zugänge (Phase 6). Nur für Admins – die Abfrage schlägt sonst fehl, und die
 * Komponente blendet sich dann aus.
 */
export const kiStandQuery = queryOptions({
  queryKey: ['ki', 'stand'],
  queryFn: () => trpc.ki.stand.query(),
  staleTime: 30_000,
  retry: false,
})

/**
 * Verfügbare Modelle eines KI-Anbieters (Phase 6).
 *
 * Lange `staleTime`: die Liste ändert sich wöchentlich, nicht minütlich, und sie kostet
 * einen Aufruf gegen den Anbieter. `retry: false`, weil eine leere Liste hier eine
 * gültige Antwort ist – die Oberfläche fällt dann auf ein Textfeld zurück.
 */
export const kiModelleQuery = (anbieter: string) =>
  queryOptions({
    queryKey: ['ki', 'modelle', anbieter],
    queryFn: () => trpc.ki.modelle.query({ anbieter: anbieter as 'openrouter' }),
    staleTime: 10 * 60_000,
    retry: false,
  })

/** Triage-Regeln (Phase 6). Nur für Admins – sonst blendet sich die Karte aus. */
export const triageRegelnQuery = queryOptions({
  queryKey: ['triage', 'regeln'],
  queryFn: () => trpc.triage.regeln.query(),
  staleTime: 15_000,
  retry: false,
})

/**
 * Der Probelauf läuft über alle offenen Aufgaben – deshalb nur auf Anforderung
 * (`enabled`) und mit kurzer Haltbarkeit: wer ihn öffnet, will den aktuellen Stand.
 */
export const triageProbelaufQuery = queryOptions({
  queryKey: ['triage', 'probelauf'],
  queryFn: () => trpc.triage.probelauf.query(),
  staleTime: 5_000,
  retry: false,
})

/**
 * Vorschau auf eine **noch nicht gespeicherte** Regel (Phase 6).
 *
 * Als Funktion, weil der Schlüssel den Entwurf tragen muss – sonst zeigt die zweite
 * Eingabe das Ergebnis der ersten. `enabled` steuert die Komponente: erst tippen, dann
 * fragen.
 */
export const triageVorschauQuery = (entwurf: {
  feld: string
  operator: string
  wert: string
  sourceId: string | null
}) =>
  queryOptions({
    queryKey: [
      'triage',
      'vorschau',
      entwurf.feld,
      entwurf.operator,
      entwurf.wert,
      entwurf.sourceId,
    ],
    queryFn: () =>
      trpc.triage.vorschau.query({
        feld: entwurf.feld as 'titel',
        operator: entwurf.operator as 'enthaelt',
        wert: entwurf.wert,
        sourceId: entwurf.sourceId,
      }),
    staleTime: 30_000,
    retry: false,
  })

/* ── Verteilung: wohin geht, was hereinkommt (Z-07.14) ───────────────── */

/**
 * **Die Verteilregeln** – workspace-frei, deshalb ohne den aktiven Workspace im Schlüssel.
 *
 * Eine Verteilregel gehört keiner Arbeitswelt; sie entscheidet, welche es wird. Den Umschalter
 * in den Schlüssel zu nehmen hieße, dieselbe Liste je Welt neu zu holen – und beim Wechsel kurz
 * eine leere zu zeigen, obwohl sich nichts geändert hat.
 */
export const verteilRegelnQuery = queryOptions({
  queryKey: ['verteilung', 'regeln'],
  queryFn: () => trpc.verteilung.regeln.query(),
  staleTime: 15_000,
  retry: false,
})

/** Ziel je Quelle und Bereich – die Einstellung, nicht die Regel. */
export const verteilZieleQuery = queryOptions({
  queryKey: ['verteilung', 'ziele'],
  queryFn: () => trpc.verteilung.ziele.query(),
  staleTime: 15_000,
  retry: false,
})

/**
 * Vorschau auf eine **noch nicht gespeicherte** Verteilregel.
 *
 * Bei der Triage ist die Vorschau eine Bequemlichkeit – dort fängt die Stufe `vorschlag` den
 * Fehlgriff. Verteilregeln haben keine Stufen, hier ist sie **die** Sicherung.
 */
export const verteilVorschauQuery = (entwurf: {
  feld: string
  operator: string
  wert: string
  sourceId: string | null
}) =>
  queryOptions({
    queryKey: [
      'verteilung',
      'vorschau',
      entwurf.feld,
      entwurf.operator,
      entwurf.wert,
      entwurf.sourceId,
    ],
    queryFn: () =>
      trpc.verteilung.vorschau.query({
        feld: entwurf.feld as 'titel',
        operator: entwurf.operator as 'enthaelt',
        wert: entwurf.wert,
        sourceId: entwurf.sourceId,
      }),
    staleTime: 30_000,
    retry: false,
  })

/**
 * **Regelentwürfe zu einer Aufgabe** – das Angebot nach dem Umhängen (E-253).
 *
 * `retry: false`, weil die Prozedur dem Admin gehört: wer sie nicht darf, bekommt einmal eine
 * Absage und danach Ruhe, statt dreimal in die Konsole zu schreiben.
 */
export const verteilEntwuerfeQuery = (taskId: string) =>
  queryOptions({
    queryKey: ['verteilung', 'entwuerfe', taskId],
    queryFn: () => trpc.verteilung.entwuerfe.query({ taskId }),
    staleTime: 60_000,
    retry: false,
  })

/**
 * **Verbrauch und Budget des laufenden Monats** (Phase 6).
 *
 * Wird häufiger frisch geholt als die übrigen KI-Abfragen: seit der Zeitgeber läuft, ändert
 * sich die Zahl auch dann, wenn niemand etwas anklickt.
 */
export const kiBudgetQuery = queryOptions({
  queryKey: ['ki', 'budget'],
  queryFn: () => trpc.ki.budget.query(),
  staleTime: 60_000,
})

/**
 * **Was auf Abnahme wartet** (Phase 9).
 *
 * Kurze Frist: wer eine Abnahme erteilt, will die Zeile sofort verschwinden sehen.
 */
export const wartendeAbnahmenQuery = queryOptions({
  queryKey: ['tasks', 'abnahmen'],
  queryFn: () => trpc.tasks.wartendeAbnahmen.query(),
  staleTime: 10_000,
})

/**
 * **Auf dem Schirm** ([Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md)) – meine Pins
 * und die Kandidaten für „ich warte auf Antwort".
 *
 * Beides **unbewertet**: ob ein Pin heute fällig ist und ob eine Wartezeile stehen bleibt,
 * rechnet `components/meintag/lage.ts` mit `@netcon-work/shared`. „Heute" ist die Frage des
 * Lesenden, nicht des Servers – bei einer Wiedervorlage ist der Tageswechsel die ganze Mechanik.
 *
 * Kurze Frist wie bei den Abnahmen: wer etwas anpinnt oder stumm stellt, will es sofort sehen.
 * Die Bestätigung kommt ohnehin optimistisch (`mutationen.ts`).
 */
export const schirmQuery = queryOptions({
  queryKey: ['schirm', 'meins'],
  queryFn: () => trpc.schirm.meins.query(),
  staleTime: 10_000,
})

export type Schirm = Awaited<ReturnType<typeof trpc.schirm.meins.query>>

/** **Meilensteine** (Phase 8) – über alle Vorhaben, für die Roadmap. */
export const meilensteineQuery = queryOptions({
  queryKey: ['vorhaben', 'meilensteine'],
  queryFn: () => trpc.vorhaben.meilensteine.query({}),
})

/** **Vorhaben** (Phase 8) – die inhaltliche Klammer, mit Stand und Pool-Anbindung. */
export const vorhabenQuery = queryOptions({
  queryKey: ['vorhaben', 'alle'],
  queryFn: () => trpc.vorhaben.alle.query(),
})

/**
 * **Die Vorhaben über alle meine Arbeitswelten** – für „Mein Tag" und das Vorhaben-Detail
 * (30.09.2026, [E-372](../../../../docs/ENTSCHEIDUNGEN.md#e-372)).
 *
 * Dieselbe Bauart wie {@link poolsAllerWeltenQuery} und aus demselben Grund: „Mein Tag" mischt
 * die Welten bewusst ([E-234](../../../../docs/ENTSCHEIDUNGEN.md#e-234)), also muss alles, was
 * dort eine Aufgabe erklärt, dieselbe Reichweite haben wie die Aufgaben selbst.
 *
 * Eine **eigene** Abfrage und kein Zusatz an `vorhabenQuery`: die Vorhaben-Ansicht bleibt an den
 * Umschalter gebunden, und beide dieselbe Antwort im Zwischenspeicher teilen zu lassen hieße,
 * dass die zuletzt geladene gewinnt. Der Schlüssel beginnt trotzdem mit `['vorhaben']`, damit
 * jede bestehende Entwertung ihn mitnimmt.
 */
export const vorhabenAllerWeltenQuery = queryOptions({
  queryKey: ['vorhaben', 'alle-welten'],
  queryFn: () => trpc.vorhaben.alle.query({ alleWelten: true }),
  staleTime: 15_000,
})

/**
 * **Die Unterhaltung an einem Vorhaben** (21.08.2026,
 * [E-205](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Je Vorhaben eine eigene Abfrage und **nicht** Teil von `vorhabenQuery`: die Übersicht zeigt
 * Karten, und zwanzig Karten müssten sonst zwanzig Unterhaltungen mitladen, um keine davon
 * anzuzeigen. Geholt wird, wenn jemand das Detail öffnet.
 */
export function vorhabenKommentareQuery(vorhabenId: string) {
  return queryOptions({
    queryKey: ['vorhaben', 'kommentare', vorhabenId],
    queryFn: () => trpc.vorhaben.kommentare.query({ vorhabenId }),
  })
}

/**
 * **Die Planung** (Phase 8): gemessener Durchsatz, Lieferkraft je Woche, Prognose je Vorhaben.
 *
 * Länger zwischengespeichert als die übrigen: die Zahlen kommen aus zwölf Wochen Log und
 * ändern sich nicht dadurch, dass jemand eine Aufgabe anklickt.
 */
export const planungQuery = queryOptions({
  queryKey: ['vorhaben', 'planung'],
  queryFn: () => trpc.vorhaben.planung.query(),
  staleTime: 5 * 60_000,
})

/**
 * **Was der Nachfluss jetzt tun würde** (Phase 8).
 *
 * Kurze Frist: die Vorschau ändert sich, sobald jemand eine Aufgabe auf „bereit" setzt oder
 * eine Anbindung ändert – und dann soll die Zahl stimmen, bevor jemand den Knopf drückt.
 */
export const nachflussVorschauQuery = queryOptions({
  queryKey: ['vorhaben', 'nachfluss'],
  queryFn: () => trpc.vorhaben.nachflussVorschau.query(),
  staleTime: 10_000,
})

/**
 * **Die letzten Sammelaktionen** (Phase 7) – für die Zeile mit dem Rückgängig-Knopf.
 *
 * Kurze Frist: nach einer Sammelaktion will man die Zeile **sofort** sehen, und wer sie
 * zurücknimmt, will es sofort verschwinden sehen.
 */
export const massenaktionenQuery = queryOptions({
  queryKey: ['eingang', 'massenaktionen'],
  queryFn: () => trpc.eingang.massenaktionen.query(),
  staleTime: 5_000,
})

/**
 * **Wie viele Vorschläge stünden bei welcher Schwelle an?** (Phase 6)
 *
 * Trägt die Zahl am Sammel-Knopf. Ohne sie wäre „übernehmen" eine Frage ins Blaue.
 */
export const vorschlagsStufenQuery = queryOptions({
  queryKey: ['eingang', 'vorschlagsStufen'],
  queryFn: () => trpc.eingang.vorschlagsStufen.query(),
})

/** Übernahmequote je Modell (Phase 6) – die Steuergröße aus Kap. 7. */
export const kiQuoteQuery = queryOptions({
  queryKey: ['ki', 'quote'],
  queryFn: () => trpc.ki.quote.query(),
  staleTime: 30_000,
  retry: false,
})

/**
 * **Anfragen aus den Fachbereichen** (Phase 9, Konzept Kap. 4.8).
 *
 * Der Server entscheidet, was zurückkommt: ein Mit-Owner bekommt seine eigenen, das Team
 * alle. Deshalb gibt es hier keinen Filter – ein Filter im Client wäre eine Sichtblende vor
 * Daten, die schon ausgeliefert sind.
 */
export const anfragenQuery = queryOptions({
  queryKey: ['anfragen', 'liste'],
  queryFn: () => trpc.anfragen.liste.query({ nurOffene: false }),
  staleTime: 15_000,
})

/**
 * **Erteilte Abnahmen mit ihren Notizen** (Phase 9, Nils am 17.08.2026).
 *
 * Aus dem Ereignis-Log gelesen, nicht aus einer Abnahme-Tabelle: die Abnahme **ist** der
 * Statuswechsel. `nurMitNotiz` entscheidet die Ansicht, nicht die Abfrage – wer die Zahl
 * ohne Notizen sehen will, sieht auch, wie oft keine geschrieben wurde.
 */
export const abnahmenQuery = (nurMitNotiz: boolean) =>
  queryOptions({
    queryKey: ['tasks', 'abnahmen', 'erteilt', nurMitNotiz],
    queryFn: () => trpc.tasks.abnahmen.query({ nurMitNotiz, tage: 180 }),
    staleTime: 30_000,
  })

/**
 * **Aktivitätsbericht einer Person** (18.08.2026, [F-45](../../../../docs/OFFENE-FRAGEN.md)).
 *
 * Nur für `steuerer` und `admin` – der Server weist alle anderen ab
 * ([E-19](../../../../docs/ENTSCHEIDUNGEN.md)). Die Ansicht zeigt den Einstieg deshalb auch
 * nur dort, wo die Personen-Auswertung ohnehin steht.
 */
export const aktivitaetQuery = (personId: string, tage: number) =>
  queryOptions({
    queryKey: ['analytics', 'aktivitaet', personId, tage],
    queryFn: () => trpc.analytics.aktivitaet.query({ personId, tage }),
    staleTime: 30_000,
    /**
     * **Kein Spinner beim Umschalten des Zeitraums** (19.08.2026, Abnahme der Phase 2).
     *
     * `tage` steht im Schlüssel – ohne `keepPreviousData` ist 90 Tage eine andere Abfrage ohne
     * Daten, und der ganze Bericht fiel beim Klick auf *30 / 90 / 180* auf **„Lädt …"** zurück,
     * bis hin zum Namen der Person in der Überschrift.
     *
     * Das ist genau der Fall, den Abnahmekriterium 5 der Phase 2 ausschließt (*„kein Spinner bei
     * Zeitraum- oder Personenwechsel"*) und Leitplanke 1 verbietet. Die zeitraumabhängigen
     * Abfragen des Leitstands tragen es seit Phase 2; der Aktivitätsbericht kam am 18.08.2026
     * dazu ([E-111](../../../../docs/ENTSCHEIDUNGEN.md)) und hat es nicht mitbekommen.
     */
    placeholderData: keepPreviousData,
  })

/**
 * **Aktivitätsfeed** (18.08.2026) – fremde Taten auf den eigenen Aufgaben.
 * Steht in „Mein Tag" und in „Mein Bereich"; der Server entscheidet, was „eigene" heißt.
 *
 * Der Zeitraum ist ein Parameter, seit das Popup zwischen 14 und 30 Tagen umschalten kann
 * (Nils, 18.08.2026). Er steht **im Schlüssel**: 30 Tage sind nicht dieselbe Antwort wie 14,
 * und wer zurückschaltet, soll die bereits geladenen 14 Tage sofort wiedersehen statt einen
 * Ladezustand. Das Präfix `tasks` bleibt, damit die bestehende Ungültigkeitserklärung über
 * `['tasks']` beide Zeiträume trifft.
 *
 * ## Warum die Vorschau 200 Zeilen holt und nicht 80
 *
 * Weil der Server **vor** dem Filtern abschneidet und der Client danach filtert – und die
 * Voreinstellung „nur Kommentare" ist. Bei 80 geladenen Ereignissen blieben am 20.08.2026
 * genau 17 Kommentare übrig, für zwölf Vorschauzeilen. Das ging knapp auf, aber nur, weil
 * gerade wenig Label-Verkehr war.
 *
 * Der Druck kam mit [E-183](../../../../docs/ENTSCHEIDUNGEN.md): seit Kommentare aus den
 * Quellsystemen als Beteiligung zählen, umfasst der Feed ein Vielfaches an Aufgaben, und
 * damit auch an Statuswechseln, die sich vor die Kommentare schieben. Gemessen für die
 * dichteste Person: 108 Ereignisse in 14 Tagen, 184 in 30.
 *
 * 200 kostet nichts – der Server gibt je Zeile nur einen 160-Zeichen-Auszug zurück, nicht
 * den Kommentar. Das Popup holt ohnehin schon 400 und ist damit für beide Zeiträume
 * vollständig.
 */
export const aktivitaetsfeedQuery = (tage = 14, limit = 200) =>
  queryOptions({
    queryKey: ['tasks', 'aktivitaetsfeed', tage, limit],
    queryFn: () => trpc.tasks.aktivitaetsfeed.query({ tage, limit }),
    staleTime: 20_000,
  })

/** Der Wochenstreifen: was läuft, mit belegtem Anfang aus dem Ereignis-Log (20.08.2026). */
export const wochenAufgabenQuery = queryOptions({
  queryKey: ['analytics', 'wochenAufgaben'],
  queryFn: () => trpc.analytics.wochenAufgaben.query(),
  staleTime: 30_000,
})

/**
 * **Der Reiter „Heute" des Leitstands** (17.09.2026, E-358) – Lage, Eingreifen und Team aus
 * einem Aufruf. Kurze Haltbarkeit und Auffrischen im Hintergrund: die Liste soll morgens
 * stimmen, ohne dass jemand neu lädt.
 */
export const leitstandHeuteQuery = queryOptions({
  queryKey: ['analytics', 'heute'],
  queryFn: () => trpc.analytics.heute.query(),
  staleTime: 30_000,
  refetchInterval: 120_000,
})

export type LeitstandHeute = Awaited<ReturnType<typeof trpc.analytics.heute.query>>

/** Zufluss, Abfluss, Durchlaufzeit je Woche – Reiter „Rückblick". */
export const flussQuery = (wochen: number) =>
  queryOptions({
    queryKey: ['analytics', 'fluss', wochen],
    queryFn: () => trpc.analytics.fluss.query({ wochen }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })

/** Was 90 Tage niemand angefasst hat – Reiter „Aufräumen". */
export const altlastenQuery = queryOptions({
  queryKey: ['analytics', 'altlasten'],
  queryFn: () => trpc.analytics.altlasten.query(),
  staleTime: 60_000,
})

/**
 * **Die KI-Vorlagen für den Anlegen-Dialog** (18.08.2026).
 *
 * Für alle Angemeldeten, nicht nur für Admins: gepflegt werden sie unter Einstellungen,
 * benutzt beim Anlegen einer Aufgabe. Lange Haltbarkeit – eine Vorlage ändert sich, wenn
 * jemand sie umschreibt, und nicht dazwischen.
 */
export const kiVorlagenQuery = queryOptions({
  queryKey: ['ki', 'vorlagen'],
  queryFn: () => trpc.ki.vorlagen.query(),
  staleTime: 5 * 60_000,
  retry: false,
})

/** Dieselben Vorlagen mit Prompt und Zählern – nur für die Verwaltung, nur für Admins. */
export const kiVorlagenVerwaltungQuery = queryOptions({
  queryKey: ['ki', 'vorlagenVerwaltung'],
  queryFn: () => trpc.ki.vorlagenVerwaltung.query(),
  staleTime: 15_000,
  retry: false,
})

/**
 * **Wen die Verwaltung auf „Gast" herabstufen würde** (18.08.2026).
 *
 * Nur für Admins. `retry: false` wie bei den übrigen Verwaltungsabfragen: wer sie nicht
 * sehen darf, soll keine drei Fehlversuche erzeugen, bevor die Karte verschwindet.
 */
export const gastVorschlaegeQuery = queryOptions({
  queryKey: ['people', 'gastVorschlaege'],
  queryFn: () => trpc.people.gastVorschlaege.query(),
  staleTime: 30_000,
  retry: false,
})

/**
 * **Der Schalter der Profil-Automatik samt Auskunft über den letzten Lauf** (20.08.2026).
 *
 * Beides in einer Abfrage, weil ein Schalter ohne Rückmeldung nicht sagt, ob er wirkt – und
 * eine Automatik, die von selbst läuft, kann nicht melden, dass sie gerade **nichts** tut.
 */
export const profilAutomatikQuery = queryOptions({
  queryKey: ['ki', 'profilAutomatik'],
  queryFn: () => trpc.ki.profilAutomatik.query(),
  staleTime: 15_000,
  retry: false,
})

/**
 * **Die Ablage der aktiven Arbeitswelt** ([Z-14](../../../../docs/phasen/zusatz-14-ablage.md)) –
 * der ganze Baum, flach, mit der aktuellen Fassung als Kopfzeile je Dokument.
 *
 * Kein `staleTime: Infinity`: der Baum ist klein, und der Live-Kanal (`dokument.geaendert`)
 * verwirft ihn ohnehin, sobald jemand etwas ändert. Beim Weltwechsel wird alles verworfen
 * (`weltWechseln`), deshalb steht die Welt nicht im Schlüssel – wie bei den Vorhaben.
 */
export const ablageQuery = queryOptions({
  queryKey: ['dokumente', 'baum'],
  queryFn: () => trpc.dokumente.baum.query(),
})

/** Ein Dokument, wie der Baum es sieht – mit Art, Elternknoten und aktueller Fassung. */
export type DokumentEintrag = Awaited<ReturnType<typeof trpc.dokumente.baum.query>>[number]

/** **Eine Fassung mit Inhalt** – ohne Nummer die aktuelle. */
export const dokumentQuery = (id: string, nummer?: number) =>
  queryOptions({
    queryKey: ['dokumente', 'lesen', id, nummer ?? 'aktuell'],
    queryFn: () => trpc.dokumente.lesen.query(nummer ? { id, nummer } : { id }),
  })

/** **Der Verlauf** eines Dokuments – alle Fassungen, neueste zuerst. */
export const dokumentVersionenQuery = (id: string) =>
  queryOptions({
    queryKey: ['dokumente', 'versionen', id],
    queryFn: () => trpc.dokumente.versionen.query({ id }),
  })
